import React, { FC, useEffect, useRef, useState } from "react";
import contentfulComponentLoop from "../../lib/contentfulComponentLoop";
import { Asset, Entry } from "contentful";
import { Container, BackgroundImage, Video, Wrapper, FooterContainer } from "./styles";
import useLocationHash from "../../hooks/useLocationHash";
import { getCloudflareImageUrl } from "../../lib/getCloudflareImageUrl";

interface SectionProps extends Entry {
  id: string,
  parentProps?: any,
  brand: string,
  isAuthenticated: boolean,
  eCommerceDisabled: boolean,
  marketHandle?: string,
  fields: {
    allowMobileOverflow?: boolean;
    anchorId?: string;
    authenticationDisplay?: string;
    backgroundColor?: string;
    backgroundImage: Asset;
    backgroundPosition: string;
    backgroundRepeat: string;
    backgroundSize: string;
    backgroundVideo?: Asset;
    components: any;
    containChildComponents: boolean;
    contentContainerMaxWidth?: number;
    eCommerceDisplay?: string;
    externalMediaUrl?: string;
    footerComponents?: any;
    footerPosition?: string;
    hideOnDesktop?: boolean;
    hideOnMobile?: boolean;
    marginBottom?: number;
    marginTop?: number;
    marketInclusion?: string[];
    mobileBackgroundImage: Asset;
    paddingBottom?: number;
    paddingTop?: number;
    scaleBackgroundOnHover?: boolean;
    sectionId: string;
    theme: string;
    useBackgroundOverlay?: boolean;
    useFullWidthLayout: boolean;
  }
}

const Section: FC<SectionProps> = ({
  id,
  parentProps,
  brand,
  fields: {
    allowMobileOverflow = false,
    anchorId = '#',
    backgroundColor,
    backgroundImage,
    backgroundPosition,
    backgroundRepeat,
    backgroundSize,
    backgroundVideo,
    components,
    containChildComponents,
    contentContainerMaxWidth,
    externalMediaUrl,
    footerComponents,
    footerPosition,
    hideOnDesktop = false,
    hideOnMobile = false,
    marginBottom,
    marginTop,
    mobileBackgroundImage,
    paddingBottom,
    paddingTop,
    scaleBackgroundOnHover = false,
    theme,
    useBackgroundOverlay,
    useFullWidthLayout
  }
}) => {
  const [scrolledTo, setScrolledTo] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const hash = useLocationHash();
  const isInPLPGrid = (parentProps && parentProps.isContentInGrid) || false;

  useEffect(() => {
    if (!sectionRef.current || !hash || !anchorId || scrolledTo) {
      return;
    }

    if (hash == `#${anchorId}`) {
      // Todo: Running into issues with scroll position changing due
      // to child components populating down the page. This works
      // for now, but it would be nice to find a non-timeout solution.
      setTimeout(() => {
        // @ts-ignore
        sectionRef.current.scrollIntoView({ behavior: 'smooth' });
        setScrolledTo(true);
      }, 250)
    }
  }, [sectionRef, hash, scrolledTo]);

  useEffect(() => {
    // override shadow dom styles for swiper components
    if (containChildComponents && !useFullWidthLayout) {
        document.querySelectorAll('swiper-container').forEach(swiper => {
            const shadowRoot = swiper.shadowRoot;
            if (shadowRoot && !shadowRoot.querySelector('#custom-swiper-style')) {
                const style = document.createElement('style');
                style.id = 'custom-swiper-style';
                style.textContent = ':host .swiper { overflow: visible !important; }';
                shadowRoot.appendChild(style);
            }
        });
    }
}, [containChildComponents, useFullWidthLayout]);

  const videoSrc = externalMediaUrl || (backgroundVideo ? `https:${backgroundVideo.fields.file?.url}` : undefined);
  const videoType = externalMediaUrl ? 'video/mp4' : backgroundVideo?.fields.file?.contentType;

  return (
    <Wrapper
      ref={sectionRef}
      id={anchorId}
      data-component-id={id}
      $allowMobileOverflow={allowMobileOverflow}
      $backgroundColor={backgroundColor}
      $backgroundPosition={backgroundPosition}
      $backgroundRepeat={backgroundRepeat}
      $backgroundSize={backgroundSize}
      $brand={brand}
      $containChildComponents={containChildComponents}
      $desktopBackgroundImageUrl={backgroundImage ? getCloudflareImageUrl(`https:${backgroundImage.fields.file?.url}`) : undefined}
      $hideOnDesktop={hideOnDesktop}
      $hideOnMobile={hideOnMobile}
      $isInPLPGrid={isInPLPGrid}
      $marginBottom={marginBottom}
      $marginTop={marginTop}
      $mobileBackgroundImageUrl={mobileBackgroundImage ? getCloudflareImageUrl(`https:${mobileBackgroundImage.fields.file?.url}`) : undefined}
      $paddingBottom={footerComponents ? 0 : paddingBottom}
      $paddingTop={paddingTop}
      $scaleBackgroundOnHover={scaleBackgroundOnHover}
      $theme={theme}
      $useBackgroundOverlay={useBackgroundOverlay}
      $useFullWidthLayout={useFullWidthLayout}
    >
      {/* Render transform-enabled background image if scaleBackgroundOnHover */}
      {scaleBackgroundOnHover && (backgroundImage || mobileBackgroundImage) && (
        <BackgroundImage
          className="bg-image"
          $desktopImageUrl={getCloudflareImageUrl(`https:${backgroundImage?.fields.file?.url}`)}
          $mobileImageUrl={
            mobileBackgroundImage
              ? getCloudflareImageUrl(`https:${mobileBackgroundImage.fields.file?.url}`)
              : undefined
          }
          $backgroundPosition={backgroundPosition}
          $backgroundSize={backgroundSize}
          $backgroundRepeat={backgroundRepeat}
        />
      )}

      {videoSrc && (
        <Video autoPlay loop muted playsInline>
          <source
            src={videoSrc}
            type={`${videoType}`}
          />
        </Video>
      )}
      <Container
        $brand={brand}
        $containChildComponents={containChildComponents}
        $contentContainerMaxWidth={contentContainerMaxWidth}
        $useFullWidthLayout={useFullWidthLayout}
        $useBackgroundOverlay={useBackgroundOverlay}
      >
        {contentfulComponentLoop(components, {
          allowMobileOverflow,
          maxWidth: contentContainerMaxWidth,
          theme,
          brand
        })}
      </Container>
      {footerComponents && footerComponents.length > 0 ? (
        <FooterContainer $footerPosition={footerPosition} $paddingTop={paddingBottom} $isInPLPGrid={isInPLPGrid}>
          {contentfulComponentLoop(footerComponents, {
            allowMobileOverflow,
            maxWidth: contentContainerMaxWidth,
            theme,
            brand
          })}
        </FooterContainer>
      ) : null}
    </Wrapper>
  );
}

export default Section;
